import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { translations, LANGS } from "@/i18n/translations";
import { pocketTranslations } from "@/i18n/pocket";
import { signupTranslations } from "@/i18n/signup";
import { preiseTranslations } from "@/i18n/preise";
import { cockpitTranslations } from "@/i18n/cockpit";
import { adminTranslations } from "@/i18n/admin";
import { pwresetTranslations } from "@/i18n/pwreset";
import { juryTranslations } from "@/i18n/jury";
import { staffTranslations } from "@/i18n/staff";
import { workspaceLabels } from "@/i18n/workspace";

const LanguageContext = createContext(null);
const SUPPORTED = LANGS.map((l) => l.code);

// Schweizer Standard: Deutsch. Englisch nur, wenn ausdrücklich gewählt (?lang=en
// oder gespeicherte Auswahl) – NICHT automatisch aus einer englischen Browsersprache.
function detectBrowserLang() {
  // Prioritätsreihenfolge für die Startsprache:
  //   1) expliziter ?lang=-Parameter  2) ausdrücklich gespeicherte Auswahl
  //   3) Schweizer Browsersprache (nur de/fr/it)  4) Fallback Deutsch
  try {
    const q = new URLSearchParams(window.location.search).get("lang");
    if (q && SUPPORTED.includes(q)) return q;
    // sc_lang_v2 wird NUR bei echter Nutzerauswahl gesetzt (siehe setLang).
    const stored = localStorage.getItem("sc_lang_v2");
    if (stored && SUPPORTED.includes(stored)) return stored;
    const cands = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
    for (const c of cands) {
      const code = String(c).slice(0, 2).toLowerCase();
      if (["de", "fr", "it"].includes(code)) return code; // Englisch NICHT auto-übernehmen
    }
  } catch { /* SSR/Privacy-Guard */ }
  return "de";
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectBrowserLang);

  useEffect(() => {
    // Nur das <html lang>-Attribut spiegeln – die Startsprache wird NICHT als
    // Nutzerauswahl persistiert, sonst würde die automatische Erkennung „einfrieren".
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l) => {
    if (SUPPORTED.includes(l)) {
      try { localStorage.setItem("sc_lang_v2", l); } catch { /* Privacy-Guard */ }
      setLangState(l);
    }
  }, []);

  const t = useMemo(() => ({ ...translations[lang], pocket: pocketTranslations[lang], signup: signupTranslations[lang], preise: preiseTranslations[lang], cockpit: cockpitTranslations[lang], admin: { ...adminTranslations[lang], labels: { ...adminTranslations[lang].labels, ...workspaceLabels[lang] } }, pwreset: pwresetTranslations[lang], jury: juryTranslations[lang], staff: staffTranslations[lang] }), [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
export { LANGS };
